/**
 * @file        evaluators/browser.js
 *              Library which implements a browser-based DCP Evaluator. This evaluator
 *              uses Web Workers to implement the base evaluator, and passes messages into
 *              the worker via the postMessage/onMessage interface.  The sandbox code is
 *              loaded from the evaluator via the importScripts() mechanism. That mechanism
 *              is disabled by the sandbox before the sandbox runs any untrusted code from
 *              the Supervisor.
 *
 * @author      Andrew Fryer, andrewf@kingsds.networka
 *              Wes Garland, wes@kingsds.network
 * @date        Aug 2020, Mar 2021
 */
'use strict';

const { DcpURL } = require('dcp/common/dcp-url');

exports.sandboxSetupDefs = require('dcp-client/generated/sandbox-definitions.json').browser;

/**
 * Generate the list of URLs that must be fetched by the Web Worker instance in order for it
 * to become a Sandbox that the Supervisor can talk to.
 *
 * The sandbox code is defined by the current dcp-client package, which is a dependency of dcp;
 * when this file is built into the dcp-client package, the file list is entrained in the
 * webpack bundle and used here, unless overridden in exports.sandboxSetupDefs.
 *
 * The sandboxSetupDefs contains a list of strings.  Each string is a dependency, and they
 * represent scripts which must be run in order.
 *
 * If the dependency string contains a '/' character, the dependency is treated as a module in an
 * NPM package; this means that we can require.resolve() it to find its location authoritatively.
 *
 * If the dependency string does not contain a '/' character, the dependency is assumed to be
 * located in the require.resolve('dcp-client/libexec/sandbox') directory.
 *
 * Note: we fake require.resolve here by using knowledge of DCP web servers' file layouts, since
 *       there is no good way to do that from inside webpack in web browser. Any third-party
 *       serving up a custom worker will need to either have the same uri path->filesystem
 *       mapping, or they will need to monkey-patch this function.
 */
exports.generateSandboxURLs = function browserEvaluator$$generateSandboxURLs() {
  const { currentScript } = document;
  var urls = [];
  var baseHref = currentScript ? new DcpURL(currentScript.src).origin : dcpConfig.scheduler.location.origin;
  var baseUrl = new DcpURL(baseHref);
  baseUrl.search = '?' + require('dcp/build').version;

  for (let def of exports.sandboxSetupDefs) {
    if (def.match(/\//))
      urls.push(baseUrl.resolve(def));
    else
      urls.push(baseUrl.resolve('dcp-client/libexec/sandbox/' + def + '.js'))
  }

  return urls;
}

/**
 * Instantiate a new Web Worker which will execute the sandbox startup code,
 * creating a DCP Evaluator.
 */
exports.BrowserEvaluator = function BrowserEvaluator(_options) {
  var options = Object.assign({}, _options);
  var hrefs = exports.generateSandboxURLs();

  if (typeof Worker === 'undefined') {
    throw new Error("Can't instantiate a browser evaluator, `Worker` is not defined.");
  }

  /* Create a script fragment which knows how to fetch the code to start the sandbox, and send it to the Web Worker
   * (evaluator). Which pieces of JavaScript to fetch from the server via importScripts() is determined from
   * generateSandboxURLs(), which in turn comes from the DCP Worker code which was embedded in this webpack bundle.
   */

  // Cache busting for debug code - this changes more often than git revision
  if (require('dcp/build').config.build === 'debug')
  {
    for (let i=0; i < hrefs.length; i++)
    {
      const url = new URL(hrefs[i]);
      url.searchParams.append('__cacheBust', String(Date.now()));
      hrefs[i] = url.href;
    }
  }

  const initSandboxCode = `importScripts(${hrefs.map(script => '"' + script + '"').join()})`;
  const blob = window.URL.createObjectURL(new Blob([initSandboxCode], { type: 'application/javascript' }));
  return new Worker(blob, options);
};
